import React, { useMemo } from 'react';
import { Stepper, Step, StepLabel, StepContent } from '@mui/material';

import { Person, HlReel, Shot, AnalysisClips } from '../../../../types';
import { isEmpty } from '../../../../utils';

import AnalyseSubStep from './analyseSubStep';
import GenerateSubStep from './generateSubStep';
import ReviewSubStep from './reviewSubStep';
import VBAccordion from './vbAccordion';

const LabelIcon = (props: {
  error?: boolean;
  completed?: boolean;
  active?: boolean;
}) => {
  const { error, completed, active } = props;

  if (error) return <div className="w-6 h-6 bg-error-900 rounded-full" />;
  if (completed) return <div className="w-6 h-6 bg-success-900 rounded-full" />;
  if (active) return <div className="w-6 h-6 bg-black rounded-full" />;
  return <div className="w-6 h-6 bg-grey-900 rounded-full" />;
};

interface Props {
  isDisabled: boolean;
  uploadedPeopleCount: number;
  uploadedClipsCount: number;
  hlReel: HlReel | null;
  analysisClips: AnalysisClips | null;
  analyseStatus: string;
  onAnalysisStart: () => void;
  requiredShots: Shot[];
  completePeople: Person[];
  incompletePeople: Person[];
  generatedPeople: Person[];
  isGenerationStage: boolean;
  handleSampleAnalysisActions: (data: any, isAfter?: boolean) => void;
  handleSampleGenerationActions: (data: any) => void;
}

const CreateStep: React.FC<Props> = (props) => {
  const {
    isDisabled,
    uploadedPeopleCount,
    uploadedClipsCount,
    hlReel,
    analysisClips,
    analyseStatus,
    onAnalysisStart,
    requiredShots,
    completePeople,
    incompletePeople,
    generatedPeople,
    isGenerationStage,
    handleSampleAnalysisActions,
    handleSampleGenerationActions,
  } = props;

  const isAnalysed = useMemo(() => {
    return analyseStatus === 'analysed';
  }, [analyseStatus]);

  const activeStep = useMemo(() => {
    if (!isAnalysed) return 0;
    else if (!isGenerationStage) return 1;
    else if (isEmpty(generatedPeople)) return 2;
    else return 3;
  }, [generatedPeople, isAnalysed, isGenerationStage]);

  const steps = [
    {
      label: 'Analyse with AI',
      content: (
        <AnalyseSubStep
          peopleCount={uploadedPeopleCount}
          clipsCount={uploadedClipsCount}
          hlReel={hlReel}
          clips={analysisClips}
          analyseStatus={analyseStatus}
          onAnalysisStart={onAnalysisStart}
          handleSampleAnalysisActions={handleSampleAnalysisActions}
        />
      ),
    },
    {
      label: 'Review',
      content: isAnalysed && (
        <ReviewSubStep
          hlReel={hlReel}
          requiredShots={requiredShots}
          completePeople={completePeople}
          incompletePeople={incompletePeople}
          generatedPeople={generatedPeople}
          isGenerationStage={isGenerationStage}
        />
      ),
    },
    {
      label: 'Generate',
      content: isGenerationStage && (
        <GenerateSubStep
          completePeople={completePeople}
          incompletePeople={incompletePeople}
          generatedPeople={generatedPeople}
          handleSampleGenerationActions={handleSampleGenerationActions}
        />
      ),
    },
  ];

  const isCompleted = activeStep >= steps.length;

  return (
    <VBAccordion
      heading="Create"
      subheading={
        isCompleted ? (
          <>
            Personalised videos of event attendees are generated
            {!isEmpty(analysisClips?.newClips) && (
              <>, but new clips were uploaded afterward</>
            )}
          </>
        ) : (
          <>Craft unique personalised videos for your event attendees</>
        )
      }
      progressText={isCompleted ? 'Completed' : `Step ${activeStep + 1} of 3`}
      isOpen={!isCompleted}
      isDisabled={isDisabled}
    >
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        sx={{ '.MuiStepConnector-line': { minHeight: '1rem' } }}
      >
        {steps.map((step, index) => {
          return (
            <Step key={step.label}>
              <StepLabel
                StepIconComponent={LabelIcon}
                sx={{
                  '.MuiStepLabel-iconContainer': { paddingRight: '20px' },
                  '.MuiStepIcon-text': { display: 'none' },
                  '& .Mui-completed > *': { color: 'var(--success-color)' },
                  '& .Mui-active > *': { color: '#000 !important' },
                  '& .Mui-disabled > *': { color: 'var(--grey)' },
                  '& .Mui-error > *': { color: 'var(--error-color)' },
                }}
              >
                <div className="text-lg font-bold">{step.label}</div>
              </StepLabel>

              {index <= activeStep && (
                <StepContent
                  sx={{ paddingLeft: '2rem', paddingRight: '0' }}
                  TransitionProps={{ in: true }}
                >
                  <div className="flex flex-col items-start gap-4">
                    {step.content}
                  </div>
                </StepContent>
              )}
            </Step>
          );
        })}
      </Stepper>
    </VBAccordion>
  );
};

export default CreateStep;
